import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import { createContext, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

export const useLenis = (): Lenis | null => useContext(LenisContext)

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)
    const router = useRouter()

    useEffect(() => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

        if (reducedMotion.matches) return

        const instance = new Lenis({
            lerp: 0.1,
            smoothWheel: true,
            autoRaf: true,
            anchors: { offset: -96 }
        })

        setLenis(instance)

        return () => {
            instance.destroy()
            setLenis(null)
        }
    }, [])

    useEffect(() => {
        if (!lenis) return

        const handleRouteChange = (url: string) => {
            if (url.includes('#')) return

            lenis.scrollTo(0, { immediate: true, force: true })
        }

        router.events.on('routeChangeComplete', handleRouteChange)

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [lenis, router.events])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}
